import type { EnvironmentEnum, OpenReplayConfig, WebTelemetryConfig } from '@ztechapps/common.contract'
import { getWindow, hasWindow } from '@ztechapps/common.utils'

// Shape of window.__ENV__ — env supplied at container runtime (server.mjs) or by
// the dev Vite plugin, so a single build serves any environment. Client-visible
// values only (public URLs/keys); secrets never leave the backend.
type RuntimeAppEnv = {
  version?: string
  environment?: EnvironmentEnum
  recaptchaSiteKey?: string
  appFullName?: string
  companyName?: string
  adminUrl?: string
  storeUrl?: string
  apiUrl?: string
  zendeskAgentUri?: string
  stripeRootUrl?: string
  stripePublishableKey?: string
}
type RuntimeEnv = {
  telemetry?: { enabled?: boolean; serviceName?: string; sampleRate?: number }
  openReplay?: { enabled?: boolean; projectKey?: string; environment?: string }
  app?: RuntimeAppEnv
}

// Runtime env injected into window.__ENV__ — by server.mjs per request in a
// container, or by the dev-only Vite plugin during `nx serve`. Deliberately no
// build-time fallback: a single built artifact must carry no environment values,
// otherwise an image built on beta serves those values after promotion to prod.
const runtimeEnv: RuntimeEnv | undefined = hasWindow()
  ? (getWindow() as unknown as { __ENV__?: RuntimeEnv }).__ENV__
  : undefined
const app = runtimeEnv?.app

export const appSettings = {
  Google: {
    recaptchaSiteKey: app?.recaptchaSiteKey,
  },
  AppFullName: app?.appFullName,
  CompanyName: app?.companyName,
  RootUrl: app?.adminUrl,
  Zendesk: {
    agentUri: app?.zendeskAgentUri,
  },
  Stripe: {
    rootUrl: app?.stripeRootUrl,
    publishableKey: app?.stripePublishableKey,
  },

  AppUrl: {
    store: app?.storeUrl,
    admin: app?.adminUrl,
    api: app?.apiUrl,
  },
  nodeEnv: app?.environment,
}

/**
 * OpenTelemetry configuration for frontend telemetry
 */
export const telemetryConfig: WebTelemetryConfig = {
  appName: 'admin',
  serviceName: runtimeEnv?.telemetry?.serviceName ?? '',
  collectorUrl: '/otel',
  enabled: runtimeEnv?.telemetry?.enabled ?? false,
  sampleRate: runtimeEnv?.telemetry?.sampleRate ?? 0.05,
  debug: import.meta.env.DEV, // Enable debug logging only in development
  serviceVersion: app?.version,
}

export const openReplayConfig: OpenReplayConfig = {
  enabled: runtimeEnv?.openReplay?.enabled ?? false,
  projectKey: runtimeEnv?.openReplay?.projectKey ?? '',
  ingestPoint: '/openreplay/ingest',
  inactivityTimeout: 10 * 60 * 1000, // 10 minutes
  // Inline CSS in both dev and prod — the OpenReplay backend (Tailscale-only on CapRover)
  // cannot reach localhost or the public admin URL to fetch stylesheets at replay time,
  // so the tracker must inline them into the recording itself.
  inlineCss: 3,
  environment: runtimeEnv?.openReplay?.environment ?? 'unknown',
}
